import { aiReportTypes } from '../types';

export const GEO_LABEL: Record<aiReportTypes.Geo, string> = {
  latam: 'Лат.Ам',
  cis: 'СНГ',
};

export const SOURCE_LABEL: Record<aiReportTypes.Source, string> = {
  telegram: 'Telegram',
  instagram: 'Instagram',
  facebook: 'Facebook',
};

export const STATUS_LABEL: Record<aiReportTypes.Status, string> = {
  queued: 'В очереди',
  running: 'Формируется',
  done: 'Готов',
  empty: 'Нет данных',
  failed: 'Ошибка',
};

export const KIND_LABEL: Record<aiReportTypes.Kind, string> = {
  page: 'Страница',
  summary: 'Сводный',
};

export function toIsoDay(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

export function periodError(range: [Date | null, Date | null]): string | null {
  const [from, to] = range;
  if (!from || !to) return 'Выберите период';
  const start = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const end = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  if (end < start) return 'Дата начала позже даты окончания';
  const days = (end - start) / 86400000 + 1;
  if (days > 14) return 'Период не длиннее 14 дней';
  const now = new Date();
  const today = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  if (end > today) return 'Будущие даты недоступны';
  return null;
}

/** Local midnight of the current UTC day, so the default period matches the server. */
export function utcToday(): Date {
  const now = new Date();
  return new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
}

export function toIsoDayUtc(ms: number): string {
  const date = new Date(ms);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${day}`;
}

export function formatUtcDate(ms: number): string {
  const date = new Date(ms);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  return `${day}.${month}.${date.getUTCFullYear()}`;
}

export function formatDateTime(ms: number): string {
  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(ms);
}

export function formatCost(costUsd?: number): string {
  if (costUsd == null) return '—';
  return `$${costUsd.toFixed(4)}`;
}

export function etaLabel(
  report: Pick<aiReportTypes.Report, 'status' | 'createdAt' | 'etaMinutes'>
): string | null {
  if (report.status !== 'queued' && report.status !== 'running') return null;
  const elapsed = (Date.now() - report.createdAt) / 60000;
  const left = Math.max(1, Math.ceil(report.etaMinutes - elapsed));
  return `Ожидаемое время: около ${left} мин`;
}

export function reportToMarkdown(report: aiReportTypes.Report): string {
  const lines = [
    `# ${report.pageName}`,
    '',
    `- Гео: ${GEO_LABEL[report.geo]}`,
    `- Источник: ${SOURCE_LABEL[report.source]}`,
    `- Период: ${formatUtcDate(report.periodFrom)} — ${formatUtcDate(report.periodTo)}`,
    `- Язык: ${report.language.toUpperCase()}`,
    `- Тип: ${KIND_LABEL[report.type]}`,
    `- Версия промта: ${report.promptVersion}`,
    '',
  ];
  if (report.result) lines.push(valueToMarkdown(report.result, 2));
  if (report.input) {
    lines.push(
      '',
      '## Входные данные',
      '',
      `- Чаты: ${report.input.chats}`,
      `- Входящие: ${report.input.messagesIn}`,
      `- Исходящие: ${report.input.messagesOut}`,
      `- Менеджеры: ${report.input.managers.join(', ') || '—'}`
    );
  }
  return lines.join('\n');
}

function valueToMarkdown(value: unknown, depth: number): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (isSection(item))
          return `${'#'.repeat(Math.min(depth, 4))} ${item.heading}\n\n${item.body}`;
        return valueToMarkdown(item, depth + 1);
      })
      .join('\n\n');
  }
  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, nested]) => {
        const heading = `${'#'.repeat(Math.min(depth, 4))} ${key}`;
        if (typeof nested === 'string') return `${heading}\n\n${nested}`;
        return `${heading}\n\n${valueToMarkdown(nested, depth + 1)}`;
      })
      .join('\n\n');
  }
  return '';
}

function isSection(value: unknown): value is { heading: string; body: string } {
  return Boolean(
    value &&
      typeof value === 'object' &&
      'heading' in value &&
      'body' in value &&
      typeof (value as { heading: unknown }).heading === 'string'
  );
}

export function printReport(report: aiReportTypes.Report): void {
  const popup = window.open('', '_blank');
  if (!popup) return;
  const body = markdownToPrintHtml(reportToMarkdown(report));
  popup.document.write(
    `<!doctype html><html><head><meta charset="utf-8"><title>${escapeHtml(report.pageName)}</title>` +
      '<style>body{font-family:Georgia,serif;padding:32px;color:#111;line-height:1.45}h1,h2,h3{font-family:sans-serif}</style>' +
      `</head><body>${body}<script>window.onload=function(){window.print()}</scr` +
      `ipt></body></html>`
  );
  popup.document.close();
}

function markdownToPrintHtml(markdown: string): string {
  return markdown
    .split('\n')
    .map((line) => {
      if (line.startsWith('### ')) return `<h3>${escapeHtml(line.slice(4))}</h3>`;
      if (line.startsWith('## ')) return `<h2>${escapeHtml(line.slice(3))}</h2>`;
      if (line.startsWith('# ')) return `<h1>${escapeHtml(line.slice(2))}</h1>`;
      if (line.startsWith('- ')) return `<p>${escapeHtml(line)}</p>`;
      if (!line) return '';
      return `<p>${escapeHtml(line)}</p>`;
    })
    .join('');
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
